import React, { createContext, ReactNode, useCallback, useMemo, useState } from "react";
import { Theme, webDarkTheme, webLightTheme } from "@fluentui/react-components";
import { StorageWrapper } from "../../services/StorageWrapper";
import { LocalStorageKeys } from "../../models/StorageKeys";

// The add-in only supports a light ("default") and a dark theme.
export const DEFAULT_THEME: "default" | "dark" = "default";

export const isThemeName = (value: unknown): value is "default" | "dark" => value === "default" || value === "dark";

export interface LightDarkContextValue {
    // The active theme name; the add-in only supports "default" (light) and "dark".
    themeName: "default" | "dark";
    // The matching Fluent UI v9 theme object, so consumers can pass `theme` straight to a
    // FluentProvider instead of repeating `themeName === "default" ? webLightTheme : webDarkTheme`.
    theme: Theme;
    // Convenience flags derived from `themeName`, so consumers stop re-deriving them locally.
    isDarkMode: boolean;
    isLightMode: boolean;
    setTheme: (theme: "default" | "dark") => void;
}

// A non-null default keeps class components reading `this.context.themeName` free of
// null-guards and gives Enzyme/Jest a sensible fallback when no provider is mounted.
export const LightDarkContext = createContext<LightDarkContextValue>({
    themeName: DEFAULT_THEME,
    theme: webLightTheme,
    isDarkMode: false,
    isLightMode: true,
    setTheme: () => undefined,
});

export interface LightDarkProviderProps {
    storageWrapper: StorageWrapper;
    children: ReactNode;
}

export const LightDarkProvider = (props: LightDarkProviderProps): JSX.Element => {
    const [themeName, setThemeState] = useState<"default" | "dark">(() => {
        const storedTheme = props.storageWrapper.loadLocalStorage(LocalStorageKeys.currentTheme);
        return isThemeName(storedTheme) ? storedTheme : DEFAULT_THEME;
    });

    const setTheme = useCallback(
        (newTheme: "default" | "dark") => {
            props.storageWrapper.saveLocalStorage(LocalStorageKeys.currentTheme, newTheme);
            setThemeState(newTheme);
        },
        [props.storageWrapper]
    );

    const contextValue = useMemo<LightDarkContextValue>(
        () => ({
            themeName,
            theme: themeName === "dark" ? webDarkTheme : webLightTheme,
            isDarkMode: themeName === "dark",
            isLightMode: themeName === "default",
            setTheme,
        }),
        [themeName, setTheme]
    );

    return <LightDarkContext.Provider value={contextValue}>{props.children}</LightDarkContext.Provider>;
};
